import { api } from './api'
import { notifyPublishingChanged, type PostConsent } from './publishing'

export type CampaignMode = 'manual' | 'autopilot'

export interface CampaignPlanRow {
  position: number
  edit_id: string
  connection_id: number
  scheduled_at: string
  source: CampaignMode
  caption: string | null
  title: string | null
  note: string | null
  /** sha256 of the rendered file: one render goes to exactly one account, once. */
  render_sha256: string | null
}

export interface CampaignPlan {
  mode: CampaignMode
  timezone: string
  start_at: string
  end_at: string
  interval_minutes: number
  jitter_minutes: number
  account_count: number | null
  seed: string
  rows: CampaignPlanRow[]
  settings: Record<string, unknown>
}

/** The consented fields that exist on ONE platform only (`consent_platform_json`).
 *
 * Everything a platform shares with the others already has a column of its own, so this
 * dictionary is exactly the remainder: a Reel's feed switch, and a Short's title,
 * visibility and two declarations. A TikTok post carries it empty, which says "nothing
 * left to record" where a missing key would say nothing at all. */
export interface ConsentPlatformFields {
  share_to_feed?: boolean
  title?: string
  visibility?: string
  made_for_kids?: boolean
  contains_synthetic_media?: boolean
}

export interface CampaignItem {
  id: string
  position: number
  edit_id: string
  edit_title: string | null
  /** Poster frame for the composer's preview; null means draw `Media` in its "no frame" state. */
  thumb_url: string | null
  duration_ms: number | null
  connection_id: number
  connection_name: string | null
  platform: string | null
  scheduled_at: string
  status: string
  source: string
  caption: string | null
  title: string | null
  error: string | null
  /** Null until this post has its own confirmation. The dispatcher will not send it.
   *
   * `platform` / `platform_fields` are the read side of session 23: the composer draws a
   * different card per platform, and re-opening a consented post must not mean guessing
   * which card it was (`app/campaigns/routes.py:_item_payload`). */
  consent: (PostConsent & {
    consented_at: string
    ui_version: string | null
    platform?: string | null
    platform_fields?: ConsentPlatformFields
  }) | null
  render_sha256: string | null
  publish_job: null | {
    id: number
    status: string
    error: string | null
    /** Machine-readable family of the failure, so the UI can explain rather than dump. */
    error_class: string | null
    terminal_reason: string | null
    /** When we will hand it over: scheduled_at minus fifteen minutes. */
    dispatch_at: string | null
    sent_at: string | null
    published_at: string | null
    refunded_at: string | null
    attempt_count: number
    /** The link to the live post — the thing the artist actually wants back from us. */
    post_url: string | null
    provider_ref: Record<string, unknown>
  }
}

/** Kadr jednego z pierwszych czterech postów na karcie (sesja 20 R2): podpisany URL z
 *  krótkim TTL albo null (edit bez miniatury — karta rysuje tło sceny); stan jednym
 *  słowem z pięciu, mapowanym w `Campaigns.tsx` na kropkę i `filter` kadru. */
export interface CampaignThumb {
  item_id: string
  edit_id: string
  thumb_url: string | null
  status: 'published' | 'scheduled' | 'awaiting_consent' | 'failed' | 'cancelled'
}

/** Konto, na które kampania publikuje, z platformą — logo na karcie (sesja 20 R2)
 *  i ze zdjęciem konta (sesja 22; null = kółko z inicjałem, jak dotąd). */
export interface CampaignAccount {
  connection_id: number
  platform: string
  display_name: string | null
  external_account_id: string | null
  avatar_url?: string | null
}

export interface CampaignSummary {
  id: string
  name: string
  mode: CampaignMode
  status: string
  timezone: string
  start_at: string | null
  end_at: string | null
  item_count: number
  status_counts: Record<string, number>
  created_at: string | null
  /** Last state change — what "paused 6 Sep" / "cancelled 14 Jul" on the list row reads. */
  updated_at?: string | null
  /** The earliest post still to go out, or null when nothing is due (paczka 12, artboard 11). */
  next_at?: string | null
  /** Posts without a stored consent — the list row's "waiting for N consents". */
  awaiting_consent?: number
  /** The account the next post goes out on (sesja 20 R2: „następny wt 10:15 · @nocna.zmiana”). */
  next_connection_id?: number | null
  /** First four posts as frames (sesja 20 R2); „+N” = item_count − thumbs.length. */
  thumbs?: CampaignThumb[]
  /** The accounts this campaign posts to, in order of first appearance in the plan. */
  accounts?: CampaignAccount[]
}

export interface CampaignDetail extends CampaignSummary {
  interval_minutes: number | null
  jitter_minutes: number
  account_count: number | null
  planner_seed: string | null
  settings: Record<string, unknown>
  activated_at: string | null
  cancelled_at: string | null
  /** One confirmation covers at most this many posts (A7). */
  consent_batch_limit: number
  /** How many posts still cannot legally be sent. Drives the composer's "N left" state. */
  awaiting_consent: number
  items: CampaignItem[]
}

export interface CampaignsState {
  enabled: boolean
  autopilot_enabled: boolean
  provider_available: boolean
  items: CampaignSummary[]
}

export interface CampaignPayload {
  name: string
  mode: CampaignMode
  edit_ids: string[]
  connection_ids: number[]
  start_at: string
  end_at?: string
  interval_minutes: number
  jitter_minutes: number
  timezone: string
  account_count?: number | null
  rows?: Array<Pick<CampaignPlanRow, 'position' | 'edit_id' | 'connection_id' | 'scheduled_at' | 'caption' | 'title'>>
}

export async function fetchCampaigns(): Promise<CampaignsState> {
  const body = await api<CampaignsState>('/api/campaigns')
  // Normalised HERE rather than at each use. The list screen already guarded its first read
  // (`state?.items ?? []`), but three later handlers call `current.items.filter/map` after a
  // create, a pause and a cancel — so the guard covered the first render and not the three
  // moments a user reaches by clicking. One place, every consumer.
  return { ...body, items: body?.items ?? [] }
}

// --------------------------------------------------------------------- kalendarz (sesja 22)

/** Konto, na które post wychodzi — ze zdjęciem, bo komórka dnia rysuje stos avatarów. */
export interface CalendarAccount {
  connection_id: number
  platform: string | null
  display_name: string | null
  avatar_url: string | null
}

/**
 * Jeden post w oknie czasu (`GET /api/campaigns/calendar`).
 *
 * `status` to te same PIĘĆ słów, którymi karta kampanii maluje kadry (`CampaignThumb`),
 * więc dzień w kalendarzu i kadr na karcie nie mają jak się rozjechać; `item_status`
 * niesie surowy stan pozycji dla ekranów, które go potrzebują.
 */
export interface CalendarItem {
  id: string
  campaign_id: string
  campaign_name: string | null
  edit_id: string
  edit_title: string | null
  thumb_url: string | null
  scheduled_at: string
  status: CampaignThumb['status']
  item_status: string
  caption: string | null
  account: CalendarAccount
  /**
   * Strefa KAMPANII, do której należy ta pozycja — dzień i godzina posta liczą się w niej,
   * a nie w strefie ostatnio utworzonej kampanii ani przeglądarki. Opcjonalna, bo serwer
   * sprzed tej sesji jej nie stempluje: wtedy zostaje strefa podana komponentowi.
   */
  timezone?: string | null
}

export interface CalendarState {
  from: string
  to: string
  items: CalendarItem[]
  /**
   * Serwer uciął listę na `max_items` pozycjach — dni PO `covered_to` są niepełne albo
   * puste, mimo że posty tam są. Bez tej flagi pusty ogon miesiąca czyta się jak „nic nie
   * jest zaplanowane”, co jest nieprawdą, którą artysta zauważy dopiero po publikacji.
   */
  truncated: boolean
  /** Ostatnia chwila, którą odpowiedź naprawdę pokrywa (ISO); `null`, gdy nic nie ucięto. */
  covered_to: string | null
  max_items?: number | null
}

/** Najdłuższe okno, o które wolno zapytać — jak `CALENDAR_MAX_DAYS` na serwerze. */
export const CALENDAR_MAX_DAYS = 62

/** Posty wszystkich kampanii konta między `from` (włącznie) a `to` (wyłącznie), obie ISO. */
export async function fetchCalendar(from: string, to: string): Promise<CalendarState> {
  const q = new URLSearchParams({ from, to })
  const body = await api<CalendarState>(`/api/campaigns/calendar?${q.toString()}`)
  return {
    from: body?.from ?? from,
    to: body?.to ?? to,
    items: body?.items ?? [],
    // Domyślnie „nic nie ucięto”: serwer, który tych pól nie zna, ma milczeć, a nie
    // straszyć paskiem o niepełnym miesiącu.
    truncated: body?.truncated ?? false,
    covered_to: body?.covered_to ?? null,
    max_items: body?.max_items ?? null,
  }
}

export async function previewCampaign(payload: CampaignPayload): Promise<{
  plan: CampaignPlan
  activation_blocked: boolean
  activation_block_reason: string | null
}> {
  return api('/api/campaigns/preview', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function createCampaign(payload: CampaignPayload): Promise<CampaignDetail> {
  const body = await api<{ campaign: CampaignDetail }>('/api/campaigns', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  notifyPublishingChanged('campaigns')
  return body.campaign
}

export async function activateCampaign(id: string): Promise<{ campaign: CampaignDetail; scheduled: number }> {
  return api(`/api/campaigns/${id}/activate`, { method: 'POST' })
}

export async function cancelCampaign(id: string): Promise<CampaignDetail> {
  const body = await api<{ campaign: CampaignDetail; cancelled_posts: number }>(
    `/api/campaigns/${id}/cancel`,
    { method: 'POST' },
  )
  notifyPublishingChanged('campaigns')
  return body.campaign
}

export async function fetchCampaign(id: string): Promise<CampaignDetail> {
  const body = await api<{ campaign: CampaignDetail }>(`/api/campaigns/${id}`)
  return body.campaign
}

// --------------------------------------------------------------------- autopilot

/**
 * Ile editow idzie na JEDNO konto i ktore konkretnie (kontrakt P, 35-R5).
 *
 * `count` bez `edit_ids` znaczy „tyle sztuk z puli, ktore konto przyjmie"; `edit_ids`
 * to jawne przypisanie z arkusza „Wybierz edity". Inwariant jest jeden i nalezy do
 * serwera: edit trafia najwyzej do JEDNEGO konta (400 `edit_in_two_accounts`), a ekran
 * pilnuje tego u siebie tylko po to, zeby nie bylo czego odrzucac.
 */
export interface PlanAccount {
  connection_id: number
  count?: number
  edit_ids?: string[]
}

/** Kolejnosc editow w planie. `series` = dlug (400 `order_unsupported`), patrz `ScheduleSheet`. */
export type PlanOrder = 'random' | 'series'

/**
 * Harmonogram planu (kontrakt P). Kazda liczba ma po drugiej stronie sufit serwera:
 * `posts_per_day` 1-5, `min_gap_minutes` >= 60, godziny dnia 06-23; ekran nosi je jako
 * ZAKRESY kontrolek (regula 54), nie jako zdania po fakcie.
 */
export interface PlanSchedule {
  start_at: string
  posts_per_day: number
  min_gap_minutes: number
  day_start_hour: number
  day_end_hour: number
  /** Utrwalone godziny („12:00"); puste = serwer rozklada sam wedlug rytmu. */
  slots?: string[]
  /** 0 = poniedzialek. Puste = wszystkie dni. */
  weekdays?: number[]
  end_mode: 'pace' | 'date'
  /** Przy `end_mode: "date"` to DATA `YYYY-MM-DD` = ostatni dzien planu (kwit PB). */
  end_at?: string
  /** 45-B (D10): `series` = udzial kazdego konta po (kolejnosc serii, pozycja w serii). */
  order?: PlanOrder
  /** 45-B: `range` (domyslny) = rytm dzienny; `weekly` = siatka `week` powtarzana `weeks` razy (D5). */
  mode?: PlanMode
  /** 45-B (D3): dolna granica postow dziennie, 1..`posts_per_day`; brak = `posts_per_day`. */
  posts_per_day_min?: number
  /** 45-B (D2): cykle dni ciszy, tylko w trybie `range`; `null` / brak = wylaczone. */
  quiet?: PlanQuiet | null
  /** 45-B (D5): wymagane przy `mode: "weekly"` — 7 list „HH:MM", 0 = poniedzialek, sloty co 5 min. */
  week?: string[][]
  /** 45-B (D5): 1..52, domyslnie 4; tylko `weekly` z `end_mode: "pace"`. */
  weeks?: number
}

/** 45-B: tryb harmonogramu planu (kontrakt „PlanSchedule”, KONTRAKTY 24.09). */
export type PlanMode = 'range' | 'weekly'

/** 45-B (D2): bieg `work_min..work_max` dni (losowany z ziarna planu), potem `quiet_len` dni ciszy.
 *  Domyslnie 5 / 7 / 1; 1 <= work_min <= work_max <= 30; 1 <= quiet_len <= 7. */
export interface PlanQuiet {
  work_min: number
  work_max: number
  quiet_len: number
}

/** 45-B: jeden post planu wskazany para (edit, konto). */
export interface PairRef {
  edit_id: string
  connection_id: number
}

/** 45-B (D9): zamiana terminow dwoch postow podgladu — film zostaje na swoim koncie z podpisem. */
export interface PlanSwap {
  a: PairRef
  b: PairRef
}

/** 45-B: powod, dla ktorego konto nie miesci swojego udzialu (D4 — mozliwe powody zaleza od trybu). */
export type PlanBlockingReason = 'limit' | 'gap' | 'window' | 'plan' | 'own_schedule'

/**
 * Zakresy kontrolek Harmonogramu jako DANE z serwera, nie stale we froncie (kwit PB).
 *
 * `day_start_hour` / `day_end_hour` to WARTOSCI domyslne okna godzin, a `day_hour_min` /
 * `day_hour_max` to jego sufity — regula 54 kaze rysowac jedno i drugie: wartosc w polu,
 * sufit jako koniec listy godzin.
 */
export interface PlanDefaults {
  /** Teraz + 2 h w strefie planu, zaokraglone W GORE do 5 min — dolny zakres godziny. */
  earliest_start_at: string
  /** Strefa, w ktorej serwer policzyl `earliest_start_at` (nieznana → UTC). */
  timezone?: string
  cap_per_day: number
  min_gap_minutes: number
  day_start_hour: number
  day_end_hour: number
  day_hour_min?: number
  day_hour_max?: number
  rhythms: Array<{ posts_per_day: number; min_gap_minutes: number }>
  /** Odstepy do wyboru we „wlasnym" rytmie (minuty). */
  gap_options?: number[]
  /** Kolejnosci, ktore serwer DZIS przyjmuje; dzis wylacznie `random`. */
  orders?: PlanOrder[]
}

/** Ile postow i kiedy wypadaja na JEDNYM koncie (odpowiedz probnego przebiegu). */
export interface PlanPerAccount {
  connection_id: number
  count: number
  first_at: string | null
  last_at: string | null
  /** Sufit konta („najwyzej 21"): ile editow puli przyjmie jego platforma. */
  cap?: number
  /** 45-B: ile editow puli przypadlo kontu (`count` = ile z nich ulozono). */
  assigned: number
  fits: boolean
  /** 45-B (D3): wyliczone tempo przy koncu „do daty"; `null` bez konca. */
  pace: { min: number; max: number } | null
  /** 45-B: posty na tydzien (tryb tygodniowy / wlasny harmonogram), inaczej `null`. */
  per_week: number | null
  blocking: { reason: PlanBlockingReason; value: number | string } | null
  /** 45-B (D4): najwczesniejsza data konca `YYYY-MM-DD`, przy ktorej konto sie miesci („Wydluz do”). */
  fits_by: string | null
  own_schedule: boolean
  /** 45-B (D7): konto w `lift_limits` tego planu. */
  lifted: boolean
  /** 45-B: limity EFEKTYWNE (po zdjeciu limitow, jesli `lifted`). */
  limits: { max_per_day: number; window_start_hour: number; window_end_hour: number; min_gap_minutes: number }
}

/** Zajete miejsce INNEGO planu: tyle postow tego konta stoi juz tego dnia. */
export interface PlanOccupied {
  day: string
  connection_id: number
  count: number
}


export interface AutopilotPayload {
  /** Either a finished batch... */
  batch_id?: string
  /** ...or an explicit set of edits. One of the two is required. */
  edit_ids?: string[]
  window_start: string
  window_end?: string
  connection_ids?: number[]
  /** Podzial puli miedzy konta (35-R5). Nowy ksztalt obok `connection_ids`, nie zamiast. */
  accounts?: PlanAccount[]
  /** Rytm, godziny dnia, dni tygodnia i koniec planu (35-R5). */
  schedule?: PlanSchedule
  timezone?: string
  name?: string
  jitter_minutes?: number
  /** One caption pack and handle for the entire publication series. */
  caption_collection_id?: number
  /** Digest from the preview: reject a save if the selected captions changed. */
  caption_plan_digest?: string
  /** Older single-value payload; the server still accepts it. */
  nick?: string
  /** Every variable the caption pack needs (N12 / P2-03). */
  values?: { artist?: string; timestamp?: string; nick?: string }
  /** Reuse the preview's selection when saving its draft. */
  seed?: string
  /** Preview only: plan it, show it, persist nothing. */
  dry_run?: boolean
  /** 45-B (D7): konta, ktorym w TYM planie zdjeto wlasne limity (zostaja limity serwera). */
  lift_limits?: number[]
  /** 45-B (D9): zamiany z Podgladu, stosowane po kolei; tylko przed uruchomieniem. */
  swaps?: PlanSwap[]
}

/**
 * Co platforma konta odrzuciła, zanim plan powstał (sesja 23, `_platform_duration_blocks`).
 *
 * Jeden wpis na KONTO, nie na edit, bo takie jest zdanie na ekranie: „2 edity pominięte
 * na @nz.backstage: Rolka do 90 s”. `max_seconds` jest pułapem TEGO konta (Rolka 90,
 * Short 180, TikTok z zapamiętanego `creator_info`) — pasek dobiera po nim zdanie.
 */
export interface PlanSkipped {
  connection_id: number
  platform: string
  edit_ids: string[]
  /** `max_duration` carries `max_seconds` (a ceiling); `min_duration` carries `min_seconds` (a floor). */
  reason: string
  max_seconds?: number
  min_seconds?: number
}

/** Turn a finished batch into a draft campaign ready for the composer.
 *
 * Nothing is armed and no gems are spent by this call: the next step is per-post
 * consent, and only an explicit activate starts the calendar.
 */
export interface AutopilotResult {
  campaign: CampaignDetail | null
  plan: CampaignPlan
  /** `{edit_id: thumb_url}` for the plan's rows — the planner itself carries no frames
   *  (paczka 12 §G 1). Missing entry = draw `Media` in its "no frame" state, never a gradient. */
  thumbs?: Record<string, string>
  /** Pary (edit, konto), których platforma konta nie przyjmie; puste = nic nie odpadło. */
  skipped?: PlanSkipped[]
  /** Koniec policzony z tempa, gdy `end_mode: "pace"`. */
  computed_end_at?: string
  /** Prawdziwe godziny slotow z rozrzutem; ekran liczy swoje tylko dopoki tych nie ma. */
  computed_slots?: string[]
  /** Czy plan miesci sie w oknie przy `end_mode: "date"`. */
  fits?: boolean
  /** Ile postow miesci sie, kiedy `fits` jest falszem. */
  fits_count?: number
  /** Ile postow ma miec plan w calosci (mianownik „15 z 24"). */
  posts_total?: number
  /** Tempo wyprowadzone przy koncu „do daty" (postow na konto na dobe). */
  computed_pace?: number
  /** Data naprawy jednym stuknieciem, `YYYY-MM-DD` („Wydluz do sob., 26 wrz"). */
  extend_to?: string | null
  /** Edity puli, ktorych plan nie ulozyl (`plan.summary.unplaced`). */
  unplaced?: string[]
  per_account?: PlanPerAccount[]
  /** Posty INNYCH planow tych kont w oknie — szare miejsca w miesiacu sceny Planera. */
  occupied?: PlanOccupied[]
  /** Koszt planu w tokenach z katalogu serwera (kontrakt C 44-R2 A); front ceny nie liczy. */
  cost_tokens?: number
  /** Odcisk pokazanego planu — `launch` go wymaga i odmawia 409 `plan_changed` przy rozjezdzie. */
  plan_digest?: string
  /** Edity planu, ktorych render trzyma inny plan tego konta (A6) — pigulka „w planie”. */
  already_planned?: Array<{ edit_id: string; campaign_id: string }>
  /** 45-B (D2): dni ciszy planu `YYYY-MM-DD` (wspolne dla kont bez wlasnego harmonogramu). */
  quiet_days?: string[]
}

/** Wpis konta w `consent.accounts[]` kontraktu C: tylko pola platformy tego konta. */
export interface LaunchConsentAccount {
  connection_id: number
  privacy_level?: string
  allow_comment?: boolean
  allow_duet?: boolean
  allow_stitch?: boolean
  disclosure_enabled?: boolean
  brand_organic?: boolean
  brand_content?: boolean
  made_for_kids?: boolean
  contains_synthetic_media?: boolean
  share_to_feed?: boolean
}

/**
 * „Zaplanuj” = koniec (44-R2 A, kontrakt C): cialo dry-runu bez `dry_run` + odcisk pokazanego
 * planu + wybory kont + zmiany z podgladu. `content_preview_confirmed` i wersje ekranu
 * wstawia SERWER (`planner-1`), klient ich nie deklaruje.
 */
export interface LaunchPayload extends Omit<AutopilotPayload, 'dry_run'> {
  plan_digest: string
  /** 45-B (D12): zgody kont biora sie z ustawien konta; `accounts` zostaje opcjonalne tylko do
   *  czasu, az PK zdejmie krok zgody w Planerze (C0 nie zmienia zachowania). */
  consent: { is_aigc: boolean; accounts?: LaunchConsentAccount[] }
  overrides?: Array<{ edit_id: string; connection_id: number; caption?: string; title?: string }>
  /** 45-B (D8): artysta uruchamia plan niepelny — serwer zapisuje tylko ulozone posty. */
  accept_partial?: boolean
}

/** 201 `launch`: `campaign` + `scheduled` + `plan.summary` rozlane przez `launch.py`. */
export interface LaunchResult extends Omit<AutopilotResult, 'campaign' | 'plan' | 'thumbs' | 'already_planned'> {
  campaign: CampaignDetail
  scheduled: number
  cost_tokens: number
  skipped?: PlanSkipped[]
  /** 45-B (D8): edity, ktore przy `accept_partial` zostaly w bibliotece. */
  unplaced_edit_ids?: string[]
}

/** Jedno zadanie: plan powstaje od razu uruchomiony, ze zgoda per post. Tokenow nie pobiera. */
export async function launchPlan(payload: LaunchPayload): Promise<LaunchResult> {
  const result = await api<LaunchResult>('/api/campaigns/launch', { method: 'POST', body: JSON.stringify(payload) })
  notifyPublishingChanged('campaigns')
  return result
}

/**
 * Zakresy kontrolek Harmonogramu. Osobna trasa, bo to DANE serwera (najwczesniejszy
 * start, sufity dnia i odstepu), a nie stale ekranu: zmiana sufitu po stronie serwera
 * ma przestawiac kontrolke, nie zostawiac uzytkownika z bledem po fakcie (regula 54).
 */
export async function fetchPlanDefaults(timezone?: string): Promise<PlanDefaults> {
  const query = timezone ? `?tz=${encodeURIComponent(timezone)}` : ''
  return api<PlanDefaults>(`/api/campaigns/plan-defaults${query}`)
}

export async function startAutopilot(payload: AutopilotPayload): Promise<AutopilotResult> {
  const result = await api<AutopilotResult>('/api/campaigns/autopilot', { method: 'POST', body: JSON.stringify(payload) })
  // `dry_run` zwraca sam plan i niczego nie zmienia; dopiero zapisany draft może
  // wprowadzić nowe posty czekające na zgodę.
  if (result.campaign) notifyPublishingChanged('campaigns')
  return result
}

// --------------------------------------------------------------------- composer

/** One post's consent AS SENT.
 *
 * Deliberately not `PostConsent`: what travels is the payload of the platform the post
 * goes out on, and only that. An Instagram item carries no `privacy_level`, because a
 * Reel has no visibility to choose and sending one anyway would ask the server to record
 * a choice the artist was never shown (`app/publishing/consent.py`).
 */
export interface ConsentRequest extends Partial<PostConsent> {
  /** Which version of the consent SCREEN collected this, recorded per post for an audit. */
  ui_version?: string
}

export interface ConsentEntry {
  item_id: string
  title?: string | null
  caption?: string | null
  consent?: ConsentRequest
}

export interface ConsentResult {
  consented: number
  ui_version: string
  remaining: number
  errors: Array<{ item_id: string | null; error: string; message?: string }>
}

/** Submit one composer batch: at most `consent_batch_limit` posts per confirmation. */
export async function submitConsent(
  id: string,
  items: ConsentEntry[],
  shared?: Partial<PostConsent>,
): Promise<ConsentResult> {
  const result = await api<ConsentResult>(`/api/campaigns/${id}/consent`, {
    method: 'POST',
    body: JSON.stringify({ items, consent: shared ?? {} }),
  })
  notifyPublishingChanged('campaigns')
  return result
}

// --------------------------------------------------------------------- item control

export async function pauseCampaign(id: string): Promise<{ campaign: CampaignDetail; paused_posts: number }> {
  return api(`/api/campaigns/${id}/pause`, { method: 'POST' })
}

export async function resumeCampaign(
  id: string,
): Promise<{ campaign: CampaignDetail; resumed: number; missed: number }> {
  return api(`/api/campaigns/${id}/resume`, { method: 'POST' })
}

export async function cancelCampaignItem(
  id: string,
  itemId: string,
): Promise<{ item: CampaignItem; cancelled_posts: number }> {
  const result = await api<{ item: CampaignItem; cancelled_posts: number }>(
    `/api/campaigns/${id}/items/${itemId}/cancel`,
    { method: 'POST' },
  )
  notifyPublishingChanged('campaigns')
  return result
}

export async function retryCampaignItem(
  id: string,
  itemId: string,
  scheduledAt?: string,
): Promise<{ item: CampaignItem }> {
  return api(`/api/campaigns/${id}/items/${itemId}/retry`, {
    method: 'POST',
    body: JSON.stringify(scheduledAt ? { scheduled_at: scheduledAt } : {}),
  })
}
